import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useLocation } from 'react-router-dom';
import RulesComparisonPreview from '../../components/RulesPlan/RulesComparisonPreview';
import RulesSelectionPreview from '../../components/RulesPlan/RulesSelectionPreview';
import ClientPlanDetailsDialog from '../../components/RulesPlan/ClientPlanDetailsDialog';
import FormSection from '../../components/shared/FormSection';
import AuditStatusConfirmDialog from '../../components/shared/AuditStatusConfirmDialog';
import {
  canManageAdminClients,
  canManagePolicyRules,
  canViewPolicyRules,
  getCurrentUserRole,
  getCurrentUserRoles,
} from '../../helpers/authSession';
import { asDisplayText, formatTemporalHumanized } from '../../helpers/dateTimeFormat';
import {
  createAdminClient,
  createClientPolicy,
  getAdminClients,
  getAdminOrganizations,
  getAdminPlanRules,
  getAdminPlans,
  getClientPolicies,
  getClientPolicyRules,
  normalizeClientPolicy,
  updateAdminClient,
  updateClientPolicy,
} from '../../services/complianceRules';
import { getRulesEngineUiMessage } from '../../services/rulesEngineErrors';

const CLIENT_STATUS_LABELS = {
  active: 'Ativo',
  suspended: 'Suspenso',
};

const resolveInitialClientForm = () => ({
  clientId: '',
  organizationId: '',
  name: '',
  status: 'active',
  planId: '',
});

const OVERRIDE_OPTIONS = ['none', 'restricted', 'approved'];
const STATUS_OPTIONS = ['active', 'inactive'];
const OVERRIDE_OPTION_LABELS = {
  none: 'Nenhum',
  restricted: 'Restrito',
  approved: 'Aprovado',
};
const STATUS_OPTION_LABELS = {
  active: 'Ativo',
  inactive: 'Inativo',
};
const SCOPE_TYPE_OPTIONS = ['organization', 'client'];
const SCOPE_TYPE_LABELS = {
  organization: 'Organização (padrão)',
  client: 'Fonte de dados específica',
};
const TRANSACTION_TYPE_OPTIONS = ['all', 'pix', 'ted', 'boleto', 'cash', 'transfer'];
const TRANSACTION_TYPE_LABELS = {
  all: 'Todos os tipos (padrão)',
  pix: 'PIX',
  ted: 'TED',
  boleto: 'Boleto',
  cash: 'Espécie',
  transfer: 'Transferência',
};
const TAB_VALUES = {
  policies: 'policies',
  clients: 'clients',
};

const resolvePolicyVersion = (policy) => asDisplayText(policy?.raw?.policy_version || policy?.raw?.policyVersion || policy?.version);
const resolveOverrideMode = (policy) => asDisplayText(policy?.raw?.override_mode || policy?.raw?.overrideMode);
const normalizeOverrideMode = (value) => (OVERRIDE_OPTIONS.includes(value) ? value : 'none');
const resolveOverrideModeLabel = (value) => OVERRIDE_OPTION_LABELS[value] || asDisplayText(value);
const resolveStatusLabel = (value) => STATUS_OPTION_LABELS[value] || asDisplayText(value);
const resolveScopeLabel = (policy) => {
  const scopeType = policy.raw?.scope_type || policy.scopeType;
  if (scopeType !== 'client') {
    return (policy.raw?.is_default ?? policy.isDefault) ? 'Organização (padrão)' : 'Organização';
  }

  const clientId = policy.raw?.client_id || policy.clientId;
  return `Fonte de dados: ${asDisplayText(clientId)}`;
};
const resolveLatestChangeSummary = (entry) => {
  if (!entry) {
    return '-';
  }

  const changedBy = asDisplayText(entry.changed_by || entry.changedBy);
  const reason = asDisplayText(entry.reason);

  if (changedBy && reason) {
    return `${changedBy} - ${reason}`;
  }

  return changedBy || reason || '-';
};
const toDateInputValue = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return '';
  }

  return text.length >= 10 ? text.slice(0, 10) : text;
};

const parseDateStart = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return null;
  }

  const parsed = new Date(`${text}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const resolveInitialFormState = () => ({
  name: '',
  planId: '',
  effectiveFrom: '',
  effectiveTo: '',
  overrideMode: 'none',
  status: 'active',
  approvalReason: '',
  transactionTypeKey: 'all',
  isDefault: false,
  priority: '100',
  scopeType: 'organization',
  clientId: '',
});

export default function PolicyGovernance() {
  const location = useLocation();
  const initialOrganizationId = React.useMemo(() => new URLSearchParams(location.search).get('organizationId') || '', [location.search]);
  const initialScopeType = React.useMemo(() => new URLSearchParams(location.search).get('scopeType') || '', [location.search]);
  const initialClientId = React.useMemo(() => new URLSearchParams(location.search).get('clientId') || '', [location.search]);
  const deepLinkAppliedRef = React.useRef(false);

  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(TAB_VALUES.policies);
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [plans, setPlans] = React.useState([]);
  const [organizations, setOrganizations] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [policies, setPolicies] = React.useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState('');
  const [formState, setFormState] = React.useState(resolveInitialFormState);
  const [editingPolicyId, setEditingPolicyId] = React.useState('');
  const [isEditingEffectiveFromLocked, setIsEditingEffectiveFromLocked] = React.useState(false);
  const [isEditingEffectiveToLocked, setIsEditingEffectiveToLocked] = React.useState(false);
  const [planRulesPreview, setPlanRulesPreview] = React.useState([]);
  const [planRulesPreviewLoading, setPlanRulesPreviewLoading] = React.useState(false);
  const [planRulesPreviewError, setPlanRulesPreviewError] = React.useState('');
  const [effectivePolicyRules, setEffectivePolicyRules] = React.useState([]);
  const [effectivePolicyRulesLoading, setEffectivePolicyRulesLoading] = React.useState(false);
  const [effectivePolicyRulesError, setEffectivePolicyRulesError] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');
  const [clientForm, setClientForm] = React.useState(resolveInitialClientForm);
  const [editingClientId, setEditingClientId] = React.useState('');
  const [clientFilterOrganizationId, setClientFilterOrganizationId] = React.useState('all');
  const [clientStatusDialog, setClientStatusDialog] = React.useState({ open: false, client: null, targetStatus: '', reason: '' });
  const [planDetailsDialog, setPlanDetailsDialog] = React.useState({ open: false, client: null });
  const [planDetailsRules, setPlanDetailsRules] = React.useState([]);
  const [planDetailsRulesLoading, setPlanDetailsRulesLoading] = React.useState(false);
  const [planDetailsRulesError, setPlanDetailsRulesError] = React.useState('');

  const canView = canViewPolicyRules();
  const canManage = canManagePolicyRules();
  // Fontes de dados sao um recurso tecnico cross-organizacao -- mesma
  // restricao que ja existia quando essa secao vivia em Organizacoes e
  // Usuarios: so ROOT/SUPERADMIN enxergam, mesmo que MANAGER tenha a
  // capability tenancy.clients.manage (que so vale dentro da propria org).
  const currentRoles = [getCurrentUserRole(), ...getCurrentUserRoles()];
  const isGlobalRole = currentRoles.some((role) => ['ROOT', 'SUPERADMIN'].includes(role));
  const canManageClients = canManageAdminClients();

  const loadData = React.useCallback(async () => {
    if (!canView) {
      setPlans([]);
      setPolicies([]);
      setOrganizations([]);
      setClients([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const [plansResponse, policiesResponse, organizationsResponse, clientsResponse] = await Promise.all([
        getAdminPlans(),
        getClientPolicies({
          organizationId: canManage ? selectedOrganizationId : '',
        }),
        getAdminOrganizations(),
        getAdminClients(),
      ]);

      setPlans(plansResponse);
      setPolicies(policiesResponse);
      setOrganizations(organizationsResponse);
      setClients(clientsResponse);
      if (!selectedOrganizationId && organizationsResponse.length) {
        const preferredOrganizationId = organizationsResponse.some((organization) => organization.id === initialOrganizationId)
          ? initialOrganizationId
          : organizationsResponse[0].id;
        setSelectedOrganizationId(preferredOrganizationId);
      }
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
      setPlans([]);
      setPolicies([]);
      setOrganizations([]);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [canManage, canView, initialOrganizationId, selectedOrganizationId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedPlan = React.useMemo(() => plans.find((plan) => plan.id === formState.planId) || null, [plans, formState.planId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadPlanRulesPreview = async () => {
      if (!formState.planId) {
        setPlanRulesPreview([]);
        setPlanRulesPreviewLoading(false);
        setPlanRulesPreviewError('');
        return;
      }

      setPlanRulesPreviewLoading(true);
      setPlanRulesPreviewError('');

      try {
        const rules = await getAdminPlanRules(formState.planId);
        if (!isMounted) {
          return;
        }

        setPlanRulesPreview(rules);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setPlanRulesPreview([]);
        setPlanRulesPreviewError(getRulesEngineUiMessage(error));
      } finally {
        if (isMounted) {
          setPlanRulesPreviewLoading(false);
        }
      }
    };

    loadPlanRulesPreview();

    return () => {
      isMounted = false;
    };
  }, [formState.planId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadEffectivePolicyRules = async () => {
      if (!editingPolicyId) {
        setEffectivePolicyRules([]);
        setEffectivePolicyRulesLoading(false);
        setEffectivePolicyRulesError('');
        return;
      }

      setEffectivePolicyRulesLoading(true);
      setEffectivePolicyRulesError('');

      try {
        const editingPolicy = policies.find((policy) => policy.id === editingPolicyId) || null;
        const organizationId = editingPolicy?.organizationId || editingPolicy?.raw?.organization_id || selectedOrganizationId;
        const rules = await getClientPolicyRules(editingPolicyId, {
          organizationId,
        });

        if (!isMounted) {
          return;
        }

        setEffectivePolicyRules(rules);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setEffectivePolicyRules([]);
        setEffectivePolicyRulesError(getRulesEngineUiMessage(error));
      } finally {
        if (isMounted) {
          setEffectivePolicyRulesLoading(false);
        }
      }
    };

    loadEffectivePolicyRules();

    return () => {
      isMounted = false;
    };
  }, [editingPolicyId, policies, selectedOrganizationId]);

  const availableClientsForOrganization = React.useMemo(
    () => clients.filter((client) => client.organizationId === selectedOrganizationId),
    [clients, selectedOrganizationId],
  );

  const activePlansForSelection = React.useMemo(() => {
    const activePlans = plans.filter((plan) => String(plan.status || '').trim().toLowerCase() === 'active');

    if (!editingPolicyId) {
      return activePlans;
    }

    const selectedExists = activePlans.some((plan) => plan.id === formState.planId);
    if (selectedExists) {
      return activePlans;
    }

    return selectedPlan ? [selectedPlan, ...activePlans] : activePlans;
  }, [editingPolicyId, formState.planId, plans, selectedPlan]);

  const draftPlansCount = React.useMemo(() => (
    plans.filter((plan) => String(plan.status || '').trim().toLowerCase() === 'draft').length
  ), [plans]);

  const filteredClients = React.useMemo(() => (
    clientFilterOrganizationId === 'all'
      ? clients
      : clients.filter((client) => client.organizationId === clientFilterOrganizationId)
  ), [clientFilterOrganizationId, clients]);

  const resetClientForm = React.useCallback(() => {
    setClientForm(resolveInitialClientForm());
    setEditingClientId('');
  }, []);

  const handleClientField = React.useCallback((field, value) => {
    setClientForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleEditClient = React.useCallback((client) => {
    setEditingClientId(client.id);
    setClientForm({
      clientId: client.clientId,
      organizationId: client.organizationId,
      name: client.name,
      status: client.status,
    });
    setErrorMessage('');
    setSuccessMessage('');
  }, []);

  const handleSubmitClient = React.useCallback(async () => {
    if (!canManageClients) {
      return;
    }

    if (!clientForm.name.trim() || !clientForm.clientId.trim()) {
      setErrorMessage('Informe o identificador e o nome da fonte de dados.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload = {
        clientId: clientForm.clientId.trim(),
        organizationId: clientForm.organizationId.trim(),
        name: clientForm.name.trim(),
        status: clientForm.status,
      };

      if (editingClientId) {
        const updatedClient = await updateAdminClient(editingClientId, payload);
        setClients((current) => current.map((client) => (client.id === editingClientId ? updatedClient : client)));
        setSuccessMessage('Fonte de dados atualizada com sucesso.');
      } else {
        // planId e' opcional -- sem ele, a fonte de dados segue a politica
        // da organizacao (sem override proprio) ate alguem criar uma
        // politica de escopo "Fonte de dados especifica" pra ela.
        const createdClient = await createAdminClient({ ...payload, planId: clientForm.planId || undefined });
        setClients((current) => [createdClient, ...current]);
        setSuccessMessage(
          clientForm.planId
            ? 'Fonte de dados criada com sucesso, com política própria no plano selecionado.'
            : 'Fonte de dados criada com sucesso. Sem plano escolhido, ela segue a política da organização.',
        );
      }

      resetClientForm();
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [canManageClients, clientForm, editingClientId, resetClientForm]);

  const handleOpenClientStatusDialog = React.useCallback((client, targetStatus) => {
    if (!canManageClients) {
      return;
    }

    setClientStatusDialog({ open: true, client, targetStatus, reason: '' });
    setErrorMessage('');
    setSuccessMessage('');
  }, [canManageClients]);

  const handleCloseClientStatusDialog = React.useCallback(() => {
    if (submitting) {
      return;
    }

    setClientStatusDialog({ open: false, client: null, targetStatus: '', reason: '' });
  }, [submitting]);

  const handleClientStatusReasonChange = React.useCallback((value) => {
    setClientStatusDialog((current) => ({ ...current, reason: value }));
  }, []);

  const handleConfirmClientStatusChange = React.useCallback(async () => {
    const { client, targetStatus, reason } = clientStatusDialog;

    if (!client || !canManageClients) {
      return;
    }

    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      setErrorMessage('Informe o motivo da alteração de status.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const updatedClient = await updateAdminClient(client.id, {
        name: client.name,
        status: targetStatus,
        reason: trimmedReason,
      });

      setClients((current) => current.map((item) => (item.id === client.id ? updatedClient : item)));
      setClientStatusDialog({ open: false, client: null, targetStatus: '', reason: '' });
      setSuccessMessage(
        targetStatus === 'active'
          ? `Fonte de dados ${client.clientId} ativada com sucesso.`
          : `Fonte de dados ${client.clientId} suspensa com sucesso.`,
      );
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [canManageClients, clientStatusDialog]);

  const handleOpenPlanDetails = React.useCallback((client) => {
    setPlanDetailsDialog({ open: true, client });
  }, []);

  const handleClosePlanDetails = React.useCallback(() => {
    setPlanDetailsDialog({ open: false, client: null });
  }, []);

  const planForDetails = React.useMemo(
    () => plans.find((plan) => plan.id === planDetailsDialog.client?.planId) || null,
    [plans, planDetailsDialog.client],
  );

  React.useEffect(() => {
    let isMounted = true;

    const loadPlanDetailsRules = async () => {
      if (!planDetailsDialog.open || !planDetailsDialog.client?.planId) {
        setPlanDetailsRules([]);
        setPlanDetailsRulesLoading(false);
        setPlanDetailsRulesError('');
        return;
      }

      setPlanDetailsRulesLoading(true);
      setPlanDetailsRulesError('');

      try {
        const rules = await getAdminPlanRules(planDetailsDialog.client.planId);
        if (!isMounted) {
          return;
        }

        setPlanDetailsRules(rules);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setPlanDetailsRules([]);
        setPlanDetailsRulesError(getRulesEngineUiMessage(error));
      } finally {
        if (isMounted) {
          setPlanDetailsRulesLoading(false);
        }
      }
    };

    loadPlanDetailsRules();

    return () => {
      isMounted = false;
    };
  }, [planDetailsDialog]);

  const resetForm = React.useCallback(() => {
    setFormState(resolveInitialFormState());
    setEditingPolicyId('');
    setIsEditingEffectiveFromLocked(false);
    setIsEditingEffectiveToLocked(false);
    setIsFormOpen(false);
  }, []);

  const handleOpenCreateForm = React.useCallback(() => {
    if (!canManage) {
      return;
    }

    setFormState(resolveInitialFormState());
    setEditingPolicyId('');
    setIsEditingEffectiveFromLocked(false);
    setIsEditingEffectiveToLocked(false);
    setErrorMessage('');
    setSuccessMessage('');
    setIsFormOpen(true);
  }, [canManage]);

  const handleFieldChange = React.useCallback((field, value) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleEditPolicy = React.useCallback((policy) => {
    const effectiveFrom = toDateInputValue(policy.raw?.effective_from);
    const effectiveTo = toDateInputValue(policy.raw?.effective_to);
    const effectiveFromDate = parseDateStart(effectiveFrom);

    setIsFormOpen(true);
    setEditingPolicyId(policy.id);
    setIsEditingEffectiveFromLocked(Boolean(effectiveFromDate && new Date() >= effectiveFromDate));
    setIsEditingEffectiveToLocked(Boolean(effectiveTo));
    setErrorMessage('');
    setSuccessMessage('');
    setFormState({
      name: policy.raw?.name || policy.name || '',
      planId: policy.planId || '',
      effectiveFrom: toDateInputValue(policy.raw?.effective_from),
      effectiveTo: toDateInputValue(policy.raw?.effective_to),
      overrideMode: normalizeOverrideMode(policy.raw?.override_mode || policy.raw?.overrideMode),
      status: policy.raw?.status || policy.status || 'active',
      approvalReason: policy.raw?.approval_reason || '',
      transactionTypeKey: policy.raw?.transaction_type_key || policy.transactionTypeKey || 'all',
      isDefault: Boolean(policy.raw?.is_default ?? policy.isDefault),
      priority: String(policy.raw?.priority ?? policy.priority ?? '100'),
      scopeType: policy.raw?.scope_type || policy.scopeType || 'organization',
      clientId: policy.raw?.client_id || policy.clientId || '',
    });
  }, []);

  React.useEffect(() => {
    if (deepLinkAppliedRef.current) {
      return;
    }

    if (initialScopeType !== 'client' || !initialClientId) {
      return;
    }

    if (!selectedOrganizationId || selectedOrganizationId !== initialOrganizationId) {
      return;
    }

    if (loading) {
      return;
    }

    deepLinkAppliedRef.current = true;

    const existingPolicy = policies.find((policy) => (
      (policy.raw?.scope_type || policy.scopeType) === 'client'
        && (policy.raw?.client_id || policy.clientId) === initialClientId
        && String(policy.raw?.status || policy.status).toLowerCase() === 'active'
    ));

    if (existingPolicy) {
      handleEditPolicy(existingPolicy);
      return;
    }

    setFormState((current) => ({
      ...current,
      scopeType: 'client',
      clientId: initialClientId,
    }));
    setIsFormOpen(true);
  }, [handleEditPolicy, initialClientId, initialOrganizationId, initialScopeType, loading, policies, selectedOrganizationId]);

  const handleSubmit = React.useCallback(async () => {
    if (!canManage) {
      return;
    }

    if (!formState.planId && !editingPolicyId) {
      setErrorMessage('Selecione um plano-base para criar a política.');
      return;
    }

    if (!selectedOrganizationId && !editingPolicyId) {
      setErrorMessage('Selecione a organização-alvo para criar a política.');
      return;
    }

    if (formState.scopeType === 'client' && !formState.clientId && !editingPolicyId) {
      setErrorMessage('Selecione a fonte de dados para criar a política.');
      return;
    }

    if (!String(formState.effectiveFrom || '').trim()) {
      setErrorMessage('A vigência inicial da política é obrigatória.');
      return;
    }

    if (!String(formState.name || '').trim()) {
      setErrorMessage('O nome da política é obrigatório.');
      return;
    }

    if (editingPolicyId && !String(formState.approvalReason || '').trim()) {
      setErrorMessage('Motivo da alteração é obrigatório para auditoria.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const editingPolicy = editingPolicyId
        ? policies.find((policy) => policy.id === editingPolicyId) || null
        : null;
      const resolvedOrganizationId = selectedOrganizationId
        || editingPolicy?.organizationId
        || editingPolicy?.raw?.organization_id
        || '';

      const payload = {
        name: formState.name,
        organizationId: resolvedOrganizationId,
        scopeType: formState.scopeType,
        clientId: formState.scopeType === 'client' ? formState.clientId : undefined,
        transactionTypeKey: formState.transactionTypeKey,
        isDefault: formState.scopeType === 'organization' ? formState.isDefault : false,
        planId: formState.planId,
        effectiveFrom: formState.effectiveFrom,
        effectiveTo: formState.effectiveTo,
        overrideMode: formState.overrideMode,
        status: formState.status,
        approvalReason: formState.approvalReason,
        reason: formState.approvalReason,
        approvedBy: 'policy_governance_ui',
      };

      if (editingPolicyId) {
        const updated = await updateClientPolicy(editingPolicyId, {
          ...payload,
          organizationId: resolvedOrganizationId,
        });
        const normalized = normalizeClientPolicy(updated);

        setPolicies((current) => current.map((policy) => (policy.id === editingPolicyId ? normalized : policy)));
        setSuccessMessage('Política atualizada com sucesso.');
      } else {
        const created = await createClientPolicy(payload);
        const normalized = normalizeClientPolicy(created);

        setPolicies((current) => [normalized, ...current]);
        setSuccessMessage('Política criada com sucesso.');
      }

      resetForm();
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [canManage, editingPolicyId, formState, policies, resetForm, selectedOrganizationId]);

  const handleDeactivate = React.useCallback(async (policy) => {
    if (!canManage) {
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const updated = await updateClientPolicy(policy.id, {
        organizationId: policy.raw?.organization_id || selectedOrganizationId,
        status: 'inactive',
        approvalReason: 'Desativacao administrativa via tela de gestao',
        reason: 'Desativacao administrativa via tela de gestao',
        approvedBy: 'policy_governance_ui',
      });

      const normalized = normalizeClientPolicy(updated);
      setPolicies((current) => current.map((item) => (item.id === policy.id ? normalized : item)));
      setSuccessMessage('Política desativada com sucesso.');
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [canManage, selectedOrganizationId]);

  const organizationNameById = React.useMemo(() => organizations.reduce((acc, organization) => {
    acc[organization.id] = organization.name;
    return acc;
  }, {}), [organizations]);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
          <Stack sx={{ alignItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Carregando governanca de policies...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography component="h1" variant="h5">Gestão de planos e políticas</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Gerencie a política padrão de cada organização. Quando uma fonte de dados específica precisar de um plano diferente, cadastre uma exceção pontual.
              </Typography>

              {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
              {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
              {!canManage ? <Alert severity="info">Seu perfil possui acesso somente leitura para esta tela.</Alert> : null}

              <Tabs value={activeTab} onChange={(event, nextTab) => setActiveTab(nextTab)} aria-label="Gestão de políticas">
                <Tab value={TAB_VALUES.policies} label="Políticas" />
                {isGlobalRole ? <Tab value={TAB_VALUES.clients} label="Fontes de Dados" /> : null}
              </Tabs>
            </Stack>
          </CardContent>
        </Card>

        {activeTab === TAB_VALUES.policies ? (
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
                  <FormControl fullWidth sx={{ maxWidth: { sm: 360 } }} disabled={submitting || Boolean(editingPolicyId)}>
                    <InputLabel id="policy-organization-label">Organização</InputLabel>
                    <Select
                      labelId="policy-organization-label"
                      value={selectedOrganizationId}
                      label="Organização"
                      onChange={(event) => setSelectedOrganizationId(event.target.value)}
                    >
                      <MenuItem value="">Selecione uma organização</MenuItem>
                      {organizations.map((organization) => (
                        <MenuItem key={organization.id} value={organization.id}>
                          {organization.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {!isFormOpen ? (
                    <Button variant="contained" onClick={handleOpenCreateForm} disabled={!canManage} sx={{ flexShrink: 0 }}>
                      Nova política
                    </Button>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            {isFormOpen ? (
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography component="h2" variant="h6">{editingPolicyId ? 'Editar política' : 'Nova política'}</Typography>

                  {editingPolicyId && isEditingEffectiveFromLocked ? <Alert severity="info">A vigência inicial não pode ser alterada após o início da política.</Alert> : null}
                  {editingPolicyId && isEditingEffectiveToLocked ? <Alert severity="info">A vigência final não pode ser alterada após ser configurada.</Alert> : null}

              <FormSection title="Escopo da política" description="Escolha se a política vale pra organização inteira ou só pra uma fonte de dados específica.">
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 1fr) minmax(240px, 1fr)' }, gap: 2, alignItems: 'start' }}>
                  <FormControl fullWidth disabled={!canManage || submitting || Boolean(editingPolicyId)}>
                    <InputLabel id="policy-scope-type-label">Escopo</InputLabel>
                    <Select
                      labelId="policy-scope-type-label"
                      value={formState.scopeType}
                      label="Escopo"
                      onChange={(event) => {
                        const nextScopeType = event.target.value;
                        handleFieldChange('scopeType', nextScopeType);
                        if (nextScopeType === 'organization') {
                          handleFieldChange('clientId', '');
                        } else {
                          handleFieldChange('isDefault', false);
                        }
                      }}
                    >
                      {SCOPE_TYPE_OPTIONS.map((scope) => (
                        <MenuItem key={scope} value={scope}>{SCOPE_TYPE_LABELS[scope]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {formState.scopeType === 'client' ? (
                    <FormControl fullWidth disabled={!canManage || submitting || Boolean(editingPolicyId)}>
                      <InputLabel id="policy-client-label">Fonte de dados</InputLabel>
                      <Select
                        labelId="policy-client-label"
                        value={formState.clientId}
                        label="Fonte de dados"
                        onChange={(event) => handleFieldChange('clientId', event.target.value)}
                      >
                        <MenuItem value="">Selecione uma fonte de dados</MenuItem>
                        {availableClientsForOrganization.map((client) => (
                          <MenuItem key={client.id} value={client.clientId}>
                            {`${client.clientId} - ${client.name}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                </Box>

                {formState.scopeType === 'organization' ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 1fr) minmax(180px, 220px)' }, gap: 2, alignItems: 'start' }}>
                    <FormControl fullWidth disabled={!canManage}>
                      <InputLabel id="policy-transaction-type-label">Tipo de transação</InputLabel>
                      <Select
                        labelId="policy-transaction-type-label"
                        value={formState.transactionTypeKey}
                        label="Tipo de transação"
                        onChange={(event) => handleFieldChange('transactionTypeKey', event.target.value)}
                      >
                        {TRANSACTION_TYPE_OPTIONS.map((value) => (
                          <MenuItem key={value} value={value}>{TRANSACTION_TYPE_LABELS[value]}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Prioridade"
                      type="number"
                      value={formState.priority}
                      onChange={(event) => handleFieldChange('priority', event.target.value)}
                      disabled={!canManage}
                    />
                  </Box>
                ) : (
                  <Alert severity="info">Tipo de transação e prioridade não se aplicam aqui — a política de uma fonte de dados específica sempre tem prioridade sobre a padrão da organização.</Alert>
                )}

                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={Boolean(formState.isDefault)}
                      onChange={(event) => handleFieldChange('isDefault', event.target.checked)}
                      disabled={!canManage || formState.scopeType === 'client'}
                    />
                  )}
                  label="Política padrão da organização"
                />
              </FormSection>

              <FormSection title="Definição da política" description="Dê um nome à política e escolha o plano-base que ela vai seguir.">
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 1.3fr) minmax(260px, 1fr)' }, gap: 2, alignItems: 'start' }}>
                  <TextField
                    fullWidth
                    label="Nome da política"
                    value={formState.name}
                    onChange={(event) => handleFieldChange('name', event.target.value)}
                    disabled={!canManage}
                    required
                  />

                  <FormControl fullWidth disabled={!canManage || Boolean(editingPolicyId)}>
                    <InputLabel id="policy-plan-label">Plano-base</InputLabel>
                    <Select
                      labelId="policy-plan-label"
                      value={formState.planId}
                      label="Plano-base"
                      onChange={(event) => handleFieldChange('planId', event.target.value)}
                    >
                      {activePlansForSelection.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                          {`${plan.code} v${plan.version} - ${plan.name}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {draftPlansCount > 0 ? (
                  <Alert severity="info">
                    {draftPlansCount} plano(s) em rascunho não aparecem aqui — ative-os em "Planos Base" antes de vinculá-los a uma política.
                  </Alert>
                ) : null}
              </FormSection>

              <FormSection title="Vigência" description="Data de início e fim. Depois que a vigência começar, a data de início não pode mais mudar.">
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Vigência inicial"
                    value={formState.effectiveFrom}
                    onChange={(event) => handleFieldChange('effectiveFrom', event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'policy-effective-from' }}
                    disabled={!canManage || isEditingEffectiveFromLocked}
                    required
                  />
                  <TextField
                    fullWidth
                    type="date"
                    label="Vigência final"
                    value={formState.effectiveTo}
                    onChange={(event) => handleFieldChange('effectiveTo', event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'policy-effective-to' }}
                    disabled={!canManage || isEditingEffectiveToLocked}
                  />
                </Stack>
              </FormSection>

              <FormSection title="Comportamento da política" description="Escolha se as regras podem ser sobrescritas manualmente e se a política está ativa.">
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControl fullWidth disabled={!canManage}>
                    <InputLabel id="policy-override-mode-label">Modo de sobrescrita</InputLabel>
                    <Select
                      labelId="policy-override-mode-label"
                      value={formState.overrideMode}
                      label="Modo de sobrescrita"
                      onChange={(event) => handleFieldChange('overrideMode', event.target.value)}
                      disabled={!canManage || isEditingEffectiveFromLocked}
                    >
                      {OVERRIDE_OPTIONS.map((mode) => (
                        <MenuItem key={mode} value={mode}>{OVERRIDE_OPTION_LABELS[mode]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {editingPolicyId && isEditingEffectiveFromLocked ? (
                    <Alert severity="info">O modo de sobrescrita não pode ser alterado após o início da política.</Alert>
                  ) : null}

                  <FormControl fullWidth disabled={!canManage}>
                    <InputLabel id="policy-status-label">Status da política</InputLabel>
                    <Select
                      labelId="policy-status-label"
                      value={formState.status}
                      label="Status da política"
                      onChange={(event) => handleFieldChange('status', event.target.value)}
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status} value={status}>{STATUS_OPTION_LABELS[status]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </FormSection>

              <FormSection title="Auditoria e impacto" description="Explique o motivo da alteração e confira quais regras a política vai aplicar de fato.">
                <TextField
                  fullWidth
                  label="Motivo da alteração (auditoria)"
                  value={formState.approvalReason}
                  onChange={(event) => handleFieldChange('approvalReason', event.target.value)}
                  disabled={!canManage}
                  placeholder="Informe o motivo para trilha de auditoria"
                />

                {selectedPlan ? (
                  <Alert severity="info">
                    Plano selecionado: {selectedPlan.name} ({selectedPlan.code})
                  </Alert>
                ) : null}

                <RulesSelectionPreview
                  title="Mapa de regras do plano-base selecionado"
                  subtitle="Use esta visão para confirmar os grupos e regras antes de criar ou editar a política."
                  rules={planRulesPreview}
                  loading={planRulesPreviewLoading}
                  errorMessage={planRulesPreviewError}
                  emptyMessage="Selecione um plano-base para visualizar as regras que serão herdadas pela policy."
                />

                {editingPolicyId ? (
                  <RulesComparisonPreview
                    baseRules={planRulesPreview}
                    effectiveRules={effectivePolicyRules}
                    loading={effectivePolicyRulesLoading}
                    errorMessage={effectivePolicyRulesError}
                  />
                ) : null}
              </FormSection>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, pt: 0.5 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!canManage || submitting}
                >
                  {editingPolicyId ? 'Salvar política' : 'Criar política'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={submitting}
                >
                  Cancelar
                </Button>
              </Stack>
                </Stack>
              </CardContent>
            </Card>
            ) : null}

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography component="h2" variant="h6">{`Políticas cadastradas (${policies.length})`}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Organizações e fontes de dados com política ativa — clique em Editar pra ajustar.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organização</TableCell>
                      <TableCell>Política</TableCell>
                      <TableCell>Plano</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Vigência</TableCell>
                      <TableCell>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {policies.map((policy) => {
                      const effectiveFrom = formatTemporalHumanized(policy.raw?.effective_from);
                      const effectiveTo = formatTemporalHumanized(policy.raw?.effective_to);
                      const planDisplayName = asDisplayText(policy.raw?.plan?.name || policy.raw?.plan?.code || policy.name);
                      const scopeType = policy.raw?.scope_type || policy.scopeType;
                      const policyVersion = resolvePolicyVersion(policy);
                      const transactionType = scopeType !== 'client'
                        ? (TRANSACTION_TYPE_LABELS[policy.raw?.transaction_type_key || policy.transactionTypeKey] || asDisplayText(policy.raw?.transaction_type_key || policy.transactionTypeKey))
                        : '';

                      return (
                        <TableRow key={policy.id} hover>
                          <TableCell>
                            <Typography variant="body2">{asDisplayText(organizationNameById[policy.raw?.organization_id] || policy.raw?.organization_id)}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{resolveScopeLabel(policy)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{asDisplayText(policy.name)}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {[policyVersion ? `rev. ${policyVersion}` : '', transactionType].filter(Boolean).join(' · ') || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{planDisplayName}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{resolveStatusLabel(policy.status)}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{`Sobrescrita: ${resolveOverrideModeLabel(resolveOverrideMode(policy))}`}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{`${effectiveFrom} - ${effectiveTo}`}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{resolveLatestChangeSummary(policy.latestChange || policy.raw?.latest_change)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handleEditPolicy(policy)}
                                disabled={!canManage || submitting}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="text"
                                size="small"
                                color="error"
                                onClick={() => handleDeactivate(policy)}
                                disabled={!canManage || submitting || String(policy.status).toLowerCase() === 'inactive'}
                              >
                                Desativar
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {!policies.length ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Nenhuma política encontrada no escopo selecionado.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </CardContent>
        </Card>
          </Stack>
        ) : null}

        {activeTab === TAB_VALUES.clients && isGlobalRole ? (
          <>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography component="h2" variant="h6">Cadastro e edição de fontes de dados</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Identifica de onde vêm os dados de transação (integração/ambiente) e quais regras se aplicam a eles. Não é uma conta de acesso humano ao painel; para isso, use Organizações e Usuários.
                  </Typography>

                  <FormSection title="Identificação" description="O identificador técnico da fonte de dados, usado para escopar políticas, regras e transações no rules-engine.">
                    <TextField
                      fullWidth
                      label="Identificador da fonte de dados (client_id)"
                      value={clientForm.clientId}
                      onChange={(event) => handleClientField('clientId', event.target.value)}
                      disabled={!canManageClients || Boolean(editingClientId) || submitting}
                      helperText="Identifica de onde vêm os dados de transação e quais regras se aplicam a eles. Não é usado para login no painel."
                    />

                    <TextField
                      fullWidth
                      label="Nome da fonte de dados"
                      value={clientForm.name}
                      onChange={(event) => handleClientField('name', event.target.value)}
                      disabled={!canManageClients || submitting}
                    />
                  </FormSection>

                  <FormSection title="Escopo" description="Associe a fonte de dados à organização correta. O status é controlado pelos botões Suspender/Ativar na listagem abaixo.">
                    <FormControl fullWidth disabled={!canManageClients || Boolean(editingClientId) || submitting}>
                      <InputLabel id="client-organization-label">Organização</InputLabel>
                      <Select
                        labelId="client-organization-label"
                        value={clientForm.organizationId}
                        label="Organização"
                        onChange={(event) => handleClientField('organizationId', event.target.value)}
                      >
                        {organizations.map((organization) => (
                          <MenuItem key={organization.id} value={organization.id}>
                            {organization.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </FormSection>

                  {!editingClientId ? (
                    <FormSection title="Política inicial (opcional)" description="Escolha um plano-base só se esta fonte de dados precisar de regras diferentes da política padrão da organização. Sem escolher, ela segue a política da organização normalmente.">
                      <FormControl fullWidth disabled={!canManageClients || submitting}>
                        <InputLabel id="client-plan-label">Plano-base</InputLabel>
                        <Select
                          labelId="client-plan-label"
                          value={clientForm.planId}
                          label="Plano-base"
                          onChange={(event) => handleClientField('planId', event.target.value)}
                        >
                          <MenuItem value="">Nenhum — segue a política da organização</MenuItem>
                          {activePlansForSelection.map((plan) => (
                            <MenuItem key={plan.id} value={plan.id}>
                              {`${plan.code} v${plan.version} - ${plan.name}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {draftPlansCount > 0 ? (
                        <Alert severity="info">
                          {draftPlansCount} plano(s) em rascunho não aparecem aqui — ative-os em "Planos Base" antes de vinculá-los a uma fonte de dados.
                        </Alert>
                      ) : null}
                    </FormSection>
                  ) : null}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, pt: 0.5 }}>
                    <Button variant="contained" onClick={handleSubmitClient} disabled={!canManageClients || submitting}>
                      {editingClientId ? 'Salvar fonte de dados' : 'Criar fonte de dados'}
                    </Button>
                    <Button variant="outlined" onClick={resetClientForm} disabled={submitting}>
                      Limpar
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography component="h2" variant="h6">{`Fontes de dados cadastradas (${filteredClients.length})`}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Use a listagem para revisar vínculos por organização, status e operações administrativas disponíveis.
                      </Typography>
                    </Stack>
                    <FormControl sx={{ minWidth: 260 }} size="small">
                      <InputLabel id="client-org-filter-label">Filtrar por organização</InputLabel>
                      <Select
                        labelId="client-org-filter-label"
                        value={clientFilterOrganizationId}
                        label="Filtrar por organização"
                        onChange={(event) => setClientFilterOrganizationId(event.target.value)}
                      >
                        <MenuItem value="all">Todas</MenuItem>
                        {organizations.map((organization) => (
                          <MenuItem key={organization.id} value={organization.id}>
                            {organization.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Identificador</TableCell>
                          <TableCell>Nome</TableCell>
                          <TableCell>Organização</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredClients.map((client) => (
                          <TableRow key={client.id}>
                            <TableCell>{client.clientId}</TableCell>
                            <TableCell>{asDisplayText(client.name)}</TableCell>
                            <TableCell>{asDisplayText(organizationNameById[client.organizationId], 'Organização não encontrada')}</TableCell>
                            <TableCell>{CLIENT_STATUS_LABELS[client.status] || asDisplayText(client.status)}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                                <Button size="small" variant="text" onClick={() => handleOpenPlanDetails(client)}>
                                  Ver detalhes do plano
                                </Button>
                                <Button size="small" variant="text" onClick={() => handleEditClient(client)} disabled={!canManageClients}>
                                  Editar
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => handleOpenClientStatusDialog(client, 'suspended')}
                                  disabled={!canManageClients || submitting || client.status !== 'active'}
                                >
                                  Suspender
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleOpenClientStatusDialog(client, 'active')}
                                  disabled={!canManageClients || submitting || client.status === 'active'}
                                >
                                  Ativar
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardContent>
            </Card>
          </>
        ) : null}
      </Stack>

      <AuditStatusConfirmDialog
        open={clientStatusDialog.open}
        title={clientStatusDialog.targetStatus === 'active' ? 'Ativar fonte de dados' : 'Suspender fonte de dados'}
        description={
          clientStatusDialog.targetStatus === 'active'
            ? `Isso vai reativar a fonte de dados "${clientStatusDialog.client?.name}". Informe o motivo para o registro de auditoria.`
            : `Isso vai suspender a fonte de dados "${clientStatusDialog.client?.name}". Informe o motivo para o registro de auditoria.`
        }
        reason={clientStatusDialog.reason}
        submitting={submitting}
        confirmColor={clientStatusDialog.targetStatus === 'active' ? 'success' : 'warning'}
        onReasonChange={handleClientStatusReasonChange}
        onClose={handleCloseClientStatusDialog}
        onConfirm={handleConfirmClientStatusChange}
      />

      <ClientPlanDetailsDialog
        open={planDetailsDialog.open}
        client={planDetailsDialog.client}
        plan={planForDetails}
        rules={planDetailsRules}
        loading={planDetailsRulesLoading}
        errorMessage={planDetailsRulesError}
        onClose={handleClosePlanDetails}
      />
    </Box>
  );
}
